'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import FxDoctorGoldBanner from '@/components/FxDoctorGoldBanner';
import LatestStrip from './LatestStrip';
import { useLang } from '@/lib/i18n/LanguageContext';
import type { PublicStats } from '@/lib/publicStats';

// Marketing landing page for logged-out visitors — design_handoff_showmetrade, screen 1a.
// Logged-in users never see this: src/app/page.tsx routes them straight to the feed.
// Copy rewritten 2026-09-05 for the people who actually sign up — beginners: "no real money",
// "a score that can't be faked", "learn from people with a track record". The invented figures
// (38.200 signals / 61% / 14 countries, "2,400+ traders", the fake Marko FX card and the fake
// forum thread) are gone: the strip shows the real counts from /api/public-stats, the card is
// labelled as an example, and the dark card explains how the score works.
//
// `initialStats` comes from the server page (src/app/page.tsx) so the counts are in the SSR HTML;
// the client fetch only runs when the prop is absent (e.g. the component is used standalone).
const nf = (n: number) => n.toLocaleString('sr-RS');

export default function LandingPage({ initialStats }: { initialStats?: PublicStats | null }) {
  const { t } = useLang();
  const [stats, setStats] = useState<PublicStats | null>(initialStats ?? null);

  useEffect(() => {
    if (initialStats !== undefined) return;
    let cancelled = false;
    fetch('/api/public-stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setStats(d); })
      .catch(() => { /* strip simply stays hidden */ });
    return () => { cancelled = true; };
  }, [initialStats]);

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh' }}>
      {/* Header — the same global Navbar every other page uses (design_handoff_layout_unifikacija,
          2026-09-01): this page used to hand-roll its own header with different links/positioning. */}
      <Navbar />
      <FxDoctorGoldBanner />

      {/* Hero */}
      <div className="grid md:grid-cols-2 gap-10 md:gap-12 px-4 sm:px-8 pt-14 pb-16 md:pt-20 md:pb-16 items-center max-w-screen-2xl mx-auto">
        <div className="flex flex-col gap-6">
          <div
            className="inline-flex self-start items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-bold"
            style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--bullish)', display: 'inline-block' }} />
            {t('Free · no real money')}
          </div>
          <h1
            className="font-heading font-extrabold"
            style={{ fontSize: 'clamp(36px, 5vw, 60px)', lineHeight: 1.06, letterSpacing: '-1.5px', color: 'var(--text-primary)', textWrap: 'balance' }}
          >
            {t('Learn to trade without risking a dinar. With a score that can’t be faked.')}
          </h1>
          <p className="text-[18px] sm:text-[19px] leading-[1.55] max-w-[500px]" style={{ color: 'var(--text-secondary)' }}>
            {t('Post a signal with an entry, a stop and a target on a simulated $10,000 account. The market closes it for you and you get an honest 0–100 score that shows exactly where you go wrong. Forex, gold, crypto — in Serbian.')}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link href="/sign-up-login-screen" className="btn-primary text-[16px] px-7 py-4">
              {t('Create a free account')}
            </Link>
            <Link href="/home-feed" className="btn-ghost text-[16px] px-7 py-4">
              {t('See the signals')}
            </Link>
          </div>
          {stats && stats.traders != null && stats.signals != null && (
            <div className="flex gap-8 mt-2 flex-wrap">
              <div>
                <div className="font-mono-nums font-bold text-[26px]" style={{ color: 'var(--text-primary)' }}>{nf(stats.traders)}</div>
                <div className="text-[13.5px] font-semibold" style={{ color: 'var(--text-muted)' }}>{t('traders')}</div>
              </div>
              <div>
                <div className="font-mono-nums font-bold text-[26px]" style={{ color: 'var(--text-primary)' }}>{nf(stats.signals)}</div>
                <div className="text-[13.5px] font-semibold" style={{ color: 'var(--text-muted)' }}>{t('published signals')}</div>
              </div>
              {stats.closed != null && (
                <div>
                  <div className="font-mono-nums font-bold text-[26px]" style={{ color: 'var(--text-primary)' }}>{nf(stats.closed)}</div>
                  <div className="text-[13.5px] font-semibold" style={{ color: 'var(--text-muted)' }}>{t('closed by the market')}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating preview cards */}
        <div className="relative hidden md:block" style={{ height: 480 }}>
          <div
            className="absolute top-0 left-8 w-full max-w-[440px] flex flex-col gap-3 p-4"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 20, boxShadow: 'var(--shadow-raised, 0 16px 40px rgba(28,43,38,0.12))' }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[14px]"
                style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}
              >
                ↗
              </div>
              <div className="flex-1">
                <div className="font-extrabold text-[11px]" style={{ color: 'var(--accent)', letterSpacing: '1px' }}>{t('EXAMPLE SIGNAL')}</div>
                <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{t('gold, 1-hour chart')}</div>
              </div>
              <div
                className="rounded-full text-[10px] font-extrabold px-2.5 py-1"
                style={{ border: '1.5px solid var(--info)', color: 'var(--info)', letterSpacing: '0.4px' }}
              >
                ● {t('OPEN')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="font-mono-nums font-bold text-[19px]" style={{ color: 'var(--text-primary)' }}>XAUUSD</div>
              <div className="badge-long text-[11px] font-extrabold rounded-lg px-2.5 py-1">▲ LONG</div>
              <div className="text-[11px] font-extrabold rounded-lg px-2.5 py-1" style={{ background: 'var(--info-muted)', color: 'var(--info)' }}>H1</div>
            </div>
            <div className="flex gap-2">
              {[
                { label: 'ENTRY', value: '4.412,40', color: 'var(--win)' },
                { label: 'SL', value: '4.398,90', color: 'var(--bearish)' },
                { label: 'TP', value: '4.452,90', color: 'var(--bullish)' },
              ].map((c) => (
                <div key={c.label} className="flex-1 rounded-lg p-2 text-center" style={{ background: 'var(--bg-base)' }}>
                  <div className="text-[10px] font-extrabold" style={{ color: 'var(--text-muted)', letterSpacing: '0.5px' }}>{c.label}</div>
                  <div className="font-mono-nums font-bold text-[14px]" style={{ color: c.color }}>{c.value}</div>
                </div>
              ))}
              <div className="flex-1 rounded-lg p-2 text-center" style={{ background: 'var(--accent-muted)' }}>
                <div className="text-[10px] font-extrabold" style={{ color: 'var(--accent)', letterSpacing: '0.5px' }}>R:R</div>
                <div className="font-mono-nums font-bold text-[14px]" style={{ color: 'var(--accent)' }}>1:3.0</div>
              </div>
            </div>
          </div>
          <div
            className="absolute bottom-0 right-0 w-full max-w-[340px] flex flex-col gap-3 p-5"
            style={{ background: 'var(--inverse-bg)', color: 'var(--inverse-text)', borderRadius: 20, boxShadow: '0 16px 40px rgba(28,43,38,0.25)' }}
          >
            <div className="text-[11px] font-extrabold" style={{ color: 'var(--inverse-muted)', letterSpacing: '1.2px' }}>{t('HOW THE SCORE WORKS')}</div>
            <ol className="flex flex-col gap-2 text-[13.5px] leading-snug font-semibold">
              {[
                t('Entry, stop and target are locked the moment you publish.'),
                t('The market closes the signal — hit TP or hit SL, nothing else.'),
                t('Every closed signal moves your 0–100 score and the leaderboard.'),
              ].map((line, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="font-mono-nums shrink-0" style={{ color: '#5ec98f' }}>0{i + 1}</span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Latest signal / analysis / forum topic — live proof the site is active */}
      <LatestStrip />

      {/* Feature cards */}
      <div className="grid md:grid-cols-3 gap-5 px-4 sm:px-8 pb-16 max-w-screen-2xl mx-auto">
        {[
          { n: '01', tone: 'accent', title: t('Practice without money'), body: t('A simulated $10,000 account, about 1% risk per signal, zero cost. A wrong call is a lesson, not a loss.') },
          { n: '02', tone: 'accent', title: t('A score that doesn’t lie'), body: t('You can’t edit a signal after the fact and you can’t post a screenshot. The leaderboard measures skill — consistency, R:R, drawdown — not who made the most.') },
          { n: '03', tone: 'win', title: t('Learn from people with a track record'), body: t('Follow traders whose score is public, ask on the forum in your own language. The Beginners section has no dumb questions.') },
        ].map((f) => (
          <div key={f.n} className="flex flex-col gap-2.5 p-7" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18 }}>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-mono-nums font-bold"
              style={{ background: f.tone === 'win' ? 'var(--win-muted)' : 'var(--accent-muted)', color: f.tone === 'win' ? 'var(--win)' : 'var(--accent)' }}
            >
              {f.n}
            </div>
            <div className="font-heading font-bold text-[19px]" style={{ color: 'var(--text-primary)' }}>{f.title}</div>
            <div className="text-[14.5px] leading-[1.55]" style={{ color: 'var(--text-secondary)' }}>{f.body}</div>
          </div>
        ))}
      </div>
      <p className="px-4 sm:px-8 -mt-8 pb-16 max-w-screen-2xl mx-auto text-[14px] font-semibold" style={{ color: 'var(--text-muted)' }}>
        <Link href="/premium" className="hover:underline">
          {t('Already have a track record? Open a premium channel and charge a subscription — the platform keeps only 10%.')}
        </Link>
      </p>

      {/* Footer: the global <SiteFooter /> in the root layout — no per-page footer here. */}
    </div>
  );
}
